import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthenticationService } from '../authentication.service';

@Component({
  selector: 'app-signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss']
})
export class SigninComponent implements OnInit {
  signinForm!: FormGroup;
  authError: string | null = null;
  socialAuthError: string | null = null;
  emailLinkInfo: string | null = null;
  isPrivacyDialogOpen = false;
  isRequirementsDialogOpen = false;
  loggedOutTitle: string | null = null;
  loggedOutSubtitle: string | null = null;

  constructor(
    public router: Router,
    private authService: AuthenticationService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.signinForm = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', Validators.required)
    });

    this.route.queryParams.subscribe(params => {
      if (params['loggedOut'] === 'true') {
        this.loggedOutTitle = 'User UID and session have ended.';
        this.loggedOutSubtitle = 'You have been successfully logged out.';
      } else {
        this.loggedOutTitle = null;
        this.loggedOutSubtitle = null;
      }
    });

    void this.bootstrapAuthLanding();
  }

  private async bootstrapAuthLanding(): Promise<void> {
    const linkResult = await this.authService.tryCompleteEmailLinkSignInFromCurrentUrl();
    if (linkResult.status === 'signed_in' || linkResult.status === 'linked') {
      await this.router.navigateByUrl(linkResult.postNavigatePath);
      return;
    }
    if (linkResult.status === 'error') {
      this.authError = linkResult.message;
    }
    await this.finishGoogleRedirectSignIn();
  }

  async onSignin(): Promise<void> {
    if (this.signinForm.invalid) {
      this.authError = 'Please fill out all required fields correctly.';
      return;
    }

    const { email, password } = this.signinForm.value;
    this.authError = null;
    this.socialAuthError = null;
    this.emailLinkInfo = null;
    this.loggedOutTitle = null;
    this.loggedOutSubtitle = null;

    try {
      await this.authService.signIn(email, password);
      await this.router.navigate(['/ai/clarity']);
    } catch (err: any) {
      console.error('Signin failed:', err);
      this.authError = 'Incorrect email or password. Please try again.';
    }
  }

  async onGoogleSignin(): Promise<void> {
    this.authError = null;
    this.socialAuthError = null;
    this.emailLinkInfo = null;
    this.loggedOutTitle = null;
    this.loggedOutSubtitle = null;
    try {
      await this.authService.signInWithGoogle();
      await this.router.navigate(['/ai/clarity']);
    } catch (err: any) {
      if (err?.code === 'auth/popup-blocked') {
        this.socialAuthError = 'Popup was blocked. Continuing with Google redirect...';
        return;
      }
      console.error('Google signin failed:', err);
      this.socialAuthError = 'Google sign-in failed. Please try again.';
    }
  }

  private async finishGoogleRedirectSignIn(): Promise<void> {
    try {
      const result = await this.authService.handleGoogleRedirectResult();
      if (result) {
        await this.router.navigate(['/ai/clarity']);
      }
    } catch (err: any) {
      console.error('Google redirect sign-in failed:', err);
      this.socialAuthError = 'Google sign-in failed after redirect. Please try again.';
    }
  }

  async onEmailLinkSignin(): Promise<void> {
    const emailControl = this.signinForm.get('email');
    const email = String(emailControl?.value || '').trim();
    emailControl?.markAsTouched();
    if (!emailControl?.valid) {
      this.authError = 'Enter a valid email address to receive a sign-in link.';
      this.emailLinkInfo = null;
      return;
    }
    this.authError = null;
    this.socialAuthError = null;
    this.emailLinkInfo = null;
    try {
      await this.authService.sendEmailSignInLink(email, {
        mode: 'signin',
        continuePath: '/auth/log-in',
        postAuthNavigatePath: '/ai/clarity'
      });
      this.emailLinkInfo =
        'Check your email for the sign-in link. Opening it on this device avoids an extra email confirmation step.';
    } catch (err: any) {
      console.error('Email link send failed:', err);
      this.authError = err?.message || 'Could not send sign-in link. Please try again.';
    }
  }

  openPrivacyDialog(): void {
    this.isPrivacyDialogOpen = true;
  }

  openRequirementsDialog(): void {
    this.isRequirementsDialogOpen = true;
  }

  closePrivacyDialog(): void {
    this.isPrivacyDialogOpen = false;
  }

  closeRequirementsDialog(): void {
    this.isRequirementsDialogOpen = false;
  }
}
